import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../contexts/AppContext';
import AdminAuth from '../../components/AdminAuth';
import NotificationCenter from '../../components/NotificationCenter';
import AlertModal from '../../components/AlertModal';
import styles from './index.module.scss';

const AuditNode = () => {
  const navigate = useNavigate();
  const { auditItems, auditProcess, isLoadingAuditItems, showAlert, alertModal, closeAlert } = useAppContext();
  const [activeTab, setActiveTab] = useState('pending');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);



  // 检查管理员认证状态
  useEffect(() => {
    const checkAuth = () => {
      const authStatus = localStorage.getItem('adminAuth');
      const authTime = localStorage.getItem('adminAuthTime');

      if (authStatus === 'true' && authTime) {
        // 检查认证是否过期（24小时）
        const currentTime = Date.now();
        const authTimestamp = parseInt(authTime);
        const hoursDiff = (currentTime - authTimestamp) / (1000 * 60 * 60);

        if (hoursDiff < 24) {
          setIsAuthenticated(true);
        } else {
          // 认证过期，清除本地存储
          localStorage.removeItem('adminAuth');
          localStorage.removeItem('adminAuthTime');
          setShowAuthModal(true);
        }
      } else {
        setShowAuthModal(true);
      }
    };

    checkAuth();
  }, []);

  const handleBack = () => {
    navigate('/');
  };

  const handleAuthSuccess = () => {
    setIsAuthenticated(true);
    setShowAuthModal(false);
  };

  const handleAuthCancel = () => {
    navigate('/');
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    localStorage.removeItem('adminAuthTime');
    setIsAuthenticated(false);
    setShowAuthModal(true);
  };

  const handleApprove = async (id) => {
    try {
      console.log('批准工艺:', id);
      await auditProcess(id, 'approve');
      await showAlert('success', '操作成功', '工艺已批准！');
    } catch (error) {
      console.error('批准工艺失败:', error);
      await showAlert('error', '操作失败', '批准失败，请重试');
    }
  };

  const handleReject = async (id) => {
    // 使用自定义弹窗替换 prompt
    const confirmed = await showAlert('confirm', '拒绝工艺', '请确认是否要拒绝此工艺？', {
      showCancel: true,
      confirmText: '确认拒绝',
      cancelText: '取消'
    });

    if (confirmed) {
      // 这里可以进一步优化为输入框弹窗，暂时使用默认原因
      const reason = '审核不通过';
      try {
        console.log('拒绝工艺:', id, '原因:', reason);
        await auditProcess(id, 'reject', reason);
        await showAlert('warning', '操作完成', '工艺已拒绝！');
      } catch (error) {
        console.error('拒绝工艺失败:', error);
        await showAlert('error', '操作失败', '拒绝失败，请重试');
      }
    }
  };



  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return '待审核';
      case 'approved': return '已批准';
      case 'rejected': return '已拒绝';
      default: return status;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'pending': return styles.statusPending;
      case 'approved': return styles.statusApproved;
      case 'rejected': return styles.statusRejected;
      default: return '';
    }
  };

  // 如果未认证，显示认证模态框
  if (showAuthModal) {
    return (
      <AdminAuth
        onAuthSuccess={handleAuthSuccess}
        onCancel={handleAuthCancel}
      />
    );
  }

  // 如果未认证且不显示模态框，显示加载状态
  if (!isAuthenticated) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <p>正在验证权限...</p>
        </div>
      </div>
    );
  }

  if (isLoadingAuditItems) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <p>正在加载审核数据...</p>
        </div>
      </div>
    );
  }

  // 计算展示列表：待审核按提交时间倒序，其它保持原顺序
  const getItemTime = (item) => new Date(item.submitTime || item.submit_time || item.created_at || 0).getTime();
  const baseList = auditItems[activeTab] || [];
  const displayList = activeTab === 'pending' ? [...baseList].sort((a, b) => getItemTime(b) - getItemTime(a)) : baseList;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <button className={styles.backButton} onClick={handleBack}>
            ← 返回主页
          </button>
          <h1>审核节点</h1>
          <button className={styles.logoutButton} onClick={handleLogout}>
            退出登录
          </button>
        </div>
      </header>

      <div className={styles.content}>
        <div className={styles.tabContainer}>
          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${activeTab === 'pending' ? styles.active : ''}`}
              onClick={() => setActiveTab('pending')}
            >
              待审核 ({auditItems.pending.length})
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'approved' ? styles.active : ''}`}
              onClick={() => setActiveTab('approved')}
            >
              已批准 ({auditItems.approved.length})
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'rejected' ? styles.active : ''}`}
              onClick={() => setActiveTab('rejected')}
            >
              已拒绝 ({auditItems.rejected.length})
            </button>
          </div>

          <div className={styles.tabContent}>
            {displayList.map(item => (
              <div key={item.id} className={`${styles.auditItem} ${(item.feedbackType || item.feedback_type) ? styles.feedbackItem : styles.processItem}`}>
                <div className={styles.itemHeader}>
                  <h3 className={styles.processName}>
                    {(item.feedbackType || item.feedback_type) && <span className={styles.feedbackIcon}>💬</span>}
                    {item.processName || item.process_name}
                  </h3>
                  <span className={`${styles.status} ${getStatusClass(item.status)}`}>
                    {getStatusText(item.status)}
                  </span>
                </div>

                <div className={styles.itemInfo}>
                  <div className={styles.infoRow}>
                    <span className={styles.label}>提交时间:</span>
                    <span>{item.submitTime || item.submit_time || item.created_at}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.label}>工艺类型:</span>
                    <span>{item.type || item.process_type}</span>
                  </div>
                  {item.approver && (
                    <div className={styles.infoRow}>
                      <span className={styles.label}>审核人:</span>
                      <span>{item.approver}</span>
                    </div>
                  )}
                  {(item.approveTime || item.approve_time) && (
                    <div className={styles.infoRow}>
                      <span className={styles.label}>审核时间:</span>
                      <span>{item.approveTime || item.approve_time}</span>
                    </div>
                  )}
                  {(item.rejectReason || item.reject_reason) && (
                    <div className={styles.infoRow}>
                      <span className={styles.label}>拒绝原因:</span>
                      <span className={styles.rejectReason}>{item.rejectReason || item.reject_reason}</span>
                    </div>
                  )}
                  {(item.feedbackType || item.feedback_type) && (
                    <>
                      <div className={styles.infoRow}>
                        <span className={styles.label}>原始问题:</span>
                        <span>{item.originalQuestion || item.original_question}</span>
                      </div>
                      <div className={styles.infoRow}>
                        <span className={styles.label}>问题ID:</span>
                        <span>{item.questionId || item.question_id}</span>
                      </div>
                    </>
                  )}
                </div>

                <div className={styles.itemDescription}>
                  <span className={styles.label}>描述:</span>
                  <p>{item.description}</p>
                </div>

                <div className={styles.itemActions}>
                  {item.status === 'pending' && (
                    <>
                      <button
                        className={styles.approveButton}
                        onClick={() => handleApprove(item.id)}
                      >
                        批准
                      </button>
                      <button
                        className={styles.rejectButton}
                        onClick={() => handleReject(item.id)}
                      >
                        拒绝
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}

            {displayList.length === 0 && (
              <div className={styles.emptyState}>
                <p>暂无{getStatusText(activeTab)}的工艺</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 通知中心 */}
      <NotificationCenter />

      {/* 弹窗组件 */}
      <AlertModal
        isOpen={alertModal.isOpen}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
        confirmText={alertModal.confirmText}
        cancelText={alertModal.cancelText}
        showCancel={alertModal.showCancel}
        onConfirm={alertModal.onConfirm}
        onCancel={alertModal.onCancel}
        onClose={closeAlert}
      />
    </div>
  );
};

export default AuditNode;
